import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InputRow } from "./InputRow";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import type { IDDItem } from "../../components/common/DropdownMenu";
import DropdownMenu from "../../components/common/DropdownMenu";
import { DownloadButton } from "../../components/common/DownloadButton";
import { CalculateButton } from "../../components/common/CalculateButton";
import { setCriteria, setInputUnit, setInputValue, validateInputs } from "../../store/slices/waterContentCalculatorSlice";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import type { IWaterContentCalculator, WaterContentCriteriaOption } from "../../interfaces/interfaces";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";

const EMPTY_INPUT_IDS: string[] = [];
const converter = new UnitConverter();

const InputsSection: React.FC = () => {
	const calculator = useAppSelector(state => state.waterContentCalculator);

	const inputIds = useMemo(() => {
		if (!calculator) {
			return EMPTY_INPUT_IDS;
		}

		return calculator.inputs.map(input => input.id);
	}, [calculator.inputs]);

	const dropDownItems: { [key: string]: IDDItem[] } = useMemo(() => {
		if (!calculator) {
			return {};
		}

		return calculator.inputs.reduce((acc, input) => {
			const units = converter.getUnitsByCategory(input.unitCategoryId);
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `common:measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));

			return acc;
		}, {} as { [key: string]: IDDItem[] });
	}, [calculator.inputs]);

	const { t } = useTranslation();
	const criteriaOptions = useMemo(() => {
		if (!calculator) {
			return [];
		}
		return calculator.criteria.options.map(option => ({
			id: option,
			text: option,
			translationKey: `water_content:criteria_options.${option}`
		} as IDDItem));
	}, [calculator.criteria.options]);

	const dispatch = useAppDispatch();
	const handleInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(setInputValue({ inputName, value }));
		},
		[dispatch]);

	const handleSelectUnit = useCallback((inputId: string, selectedUnitId: string) => {
		if (!calculator) {
			return;
		}

		dispatch(setInputUnit({ inputId, toUnitId: selectedUnitId }));

	}, [dispatch, calculator]);

	const handleSelectCriteria = useCallback((selectedCriteria: string) => {
		if (!calculator) {
			return;
		}

		dispatch(
			setCriteria(selectedCriteria as WaterContentCriteriaOption)
		);
	}, [dispatch, calculator]);

	const [triggerCalculator, { isLoading }] = useCalculatorMutation();

	const validated = (freshCalc: IWaterContentCalculator) => {
		return !freshCalc.inputs.some(input => input.error != null);
	};

	const handleCalculate = useCallback(async () => {
		if (!calculator) {
			return;
		}

		dispatch(validateInputs());

		const formData = getFormData();
		if (!formData) {
			return;
		}

		triggerCalculator({
			calculator_type: "water_content",
			...formData,
		});
	}, [calculator, triggerCalculator]);

	const getFormData = useCallback(() => {
		if (!calculator || !validated(calculator)) {
			return;
		}
		const formData: Record<string, number | string> = {};
		calculator.inputs.forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});
		formData['criteria'] = calculator.criteria.selected;

		return formData;
	}, [calculator]);

	return (
		<div className="water-content__inputs-section">
			<fieldset className="water-content__inputs round-corner-container">
				<legend>{t('main_parameters')}</legend>
				<div className="water-content__criteria">
					{/* <div className="water-content__criteria-label">{t('water_content:criteria_label')}:</div> */}
					<DropdownMenu
						inputId={calculator.criteria.id}
						items={criteriaOptions}
						selectedItemId={calculator.criteria.selected}
						onSelect={handleSelectCriteria}
					/>
				</div>

				{inputIds.map((inputId) => (
					<InputRow
						key={inputId}
						inputId={inputId}
						dropdownItems={dropDownItems[inputId]}
						onInputChange={handleInputChange}
						onSelectUnit={handleSelectUnit}
					/>
				))}
			</fieldset>
			<div className="water-content__buttons">
				<CalculateButton onClick={handleCalculate} disabled={isLoading} />
				<DownloadButton getFormData={getFormData} calculatorType="water_content" disabled={false} title={t(`download`)} />
			</div>
		</div>
	);
};

export default memo(InputsSection);
